import axios from "axios";

const normalizeSortingOrderForApi = (sortingOrder) => {
  const value = String(sortingOrder || "").trim().toLowerCase();

  if (value === "asc" || value === "ascending") {
    return "ascending";
  }

  if (value === "desc" || value === "descending") {
    return "descending";
  }

  return "";
};

const useFetchData = ({
  pageNumber,
  sortingColumnName,
  sortingOrder,
  endpoint,
  setData,
  setPaginationData,
  idName,
  idValue,
  method,
  searchQuery,
  statusFilterQuery,
  startDateQuery,
  endDateQuery,
  auth_token,
  dispatch,
  tableProps,
  setTableProps,
  idParams,
  allData,
  bodyData,
  preserveApiSortOrder = false,
}) => {
  let abortController = new AbortController();

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchData = async () => {
    try {
      abortController.abort();

      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setTableProps &&
          setTableProps({
            ...tableProps,
            isLoading: true,
            errorMessage: "",
          });

        let setDataResult;
        if (dispatch) {
          dispatch(setData([]));
        } else if (allData) {
          setDataResult = await setData({}, {});
        } else {
          setDataResult = await setData([], {});
        }

        setPaginationData(
          setDataResult?.paginationData ? setDataResult.paginationData : {}
        );

        setTableProps &&
          setTableProps({
            ...tableProps,
            isLoading: false,
            errorMessage: "",
          });

        return;
      }

      const formData = new FormData();
      formData.append("page", pageNumber);
      idParams
        ? idParams?.forEach((e) => formData.append(e.key, e.value))
        : formData.append(idName, idValue);
      searchQuery && formData.append("search", searchQuery);
      statusFilterQuery && formData.append("statusFilterQuery", statusFilterQuery);
      startDateQuery && formData.append("start_date", startDateQuery);
      endDateQuery && formData.append("end_date", endDateQuery);
      sortingColumnName &&
        formData.append("sorting_column_name", sortingColumnName);
      const normalizedSortingOrder = normalizeSortingOrderForApi(sortingOrder);
      normalizedSortingOrder &&
        formData.append("sorting_order", normalizedSortingOrder);

      const newAbortController = new AbortController();
      abortController = newAbortController;

      setTableProps &&
        setTableProps({
          ...tableProps,
          isLoading: true,
          errorMessage: "",
        });

      await axios({
        method: method || "POST",
        url: endpoint,
        data: bodyData ? bodyData : formData,
        headers: {
          Authorization: `Bearer ${auth_token}`,
        },
        signal: newAbortController.signal,
      })
        .then(async (res) => {
          const responseData = res?.data;

          // ✅ Extract results
          let results = responseData?.results || [];

          // ✅ Format `created_on` if available
          results = results.map((item) => ({
            ...item,
            department: item.department === "O" ? "Others" : item.department || "-",
            created_on: item.created_on ? formatDate(item.created_on) : "-",
          }));

          // ✅ Optional — sort newest first
          if (!preserveApiSortOrder) {
            results.sort(
              (a, b) =>
                new Date(b.created_on).getTime() -
                new Date(a.created_on).getTime()
            );
          }

          // ✅ Push to Redux or state
          let setDataResult;
          if (dispatch) {
            dispatch(setData(results));
          } else if (allData) {
            setDataResult = await setData(responseData, responseData);
          } else {
            setDataResult = await setData(results, responseData);
          }

          setPaginationData(
            setDataResult?.paginationData ? setDataResult.paginationData : responseData
          );

          setTableProps &&
            setTableProps({
              ...tableProps,
              isLoading: false,
              errorMessage: "",
            });
        })
        .catch((err) => {
          const isCanceled =
            err?.name === "CanceledError" ||
            err?.code === "ERR_CANCELED" ||
            String(err?.message || "").toLowerCase().includes("canceled");
          if (isCanceled) {
            return;
          }
          console.log(err);
          dispatch ? dispatch(setData([])) : setData([]);
          setPaginationData();

          setTableProps &&
            setTableProps({
              ...tableProps,
              isLoading: false,
              errorMessage: generateErrorMessage(
                err,
                "Network error! please try again later."
              ),
            });
        });
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request aborted:", error.message);
      } else {
        console.log(error);
        dispatch ? dispatch(setData([])) : setData([]);
        setPaginationData();
        console.log(error?.response?.data?.response);
      }
    }
  };

  fetchData();

  const generateErrorMessage = (error, fallbackMessage) => {
    return typeof error?.response?.data?.response === "object" || searchQuery
      ? `"${searchQuery}" not found!`
      : error?.response?.data?.response || fallbackMessage;
  };

  return () => {
    abortController.abort();
  };
};

export default useFetchData;
